/**
 * The batching half of the card-seen beacon, kept free of React and of the DOM
 * so it can be tested for the properties that actually matter: that a card is
 * reported once, that a burst of rows costs one request, and that nothing is
 * left unsent when the page goes away.
 *
 * Directive-free on purpose (repo anti-pattern #18): the hook that uses this is
 * a `"use client"` module, and every export of one is a throwing proxy on the
 * server. Nothing here touches the browser, so nothing here should be able to
 * take a server render down.
 */

/**
 * How much of a row must be showing, and for how long, before it counts.
 *
 * Half the row is the smallest fraction that cannot be satisfied by a card
 * clipped to a sliver at the edge of the viewport during a fast scroll — which
 * is the thing that would turn "flicked past" into "seen" and quietly answer
 * the question this instrument exists to ask. One second is the same argument
 * in time: a flick through a list of thirty cards must not mark thirty seen.
 */
export const SEEN_VISIBLE_RATIO = 0.5;
export const SEEN_DWELL_MS = 1000;

/** Quiet period before a batch is sent. Long enough to coalesce a scroll. */
export const SEEN_FLUSH_MS = 2000;

/** Hard cap per request, matching the server's own cap. */
export const SEEN_MAX_BATCH = 200;

/**
 * Ids already reported in this page's lifetime.
 *
 * Module-level rather than per-hook: the same card is rendered by the list and
 * by the detail pane, a row unmounts and remounts on every optimistic update,
 * and none of that is a second sighting. The server is write-once anyway, so
 * this is about not sending the request at all.
 */
const reported = new Set<string>();

/**
 * Ids that have passed the dwell on any card surface in this page's lifetime,
 * whether or not the beacon has gone yet.
 *
 * Read by the approval-card probe (`staged-probe.ts`), which otherwise looks
 * only at the Mucka bar and panel. Since TKT-1472 the agent sends the owner to
 * the Actions page for a card they cannot see in the bar, so a card on screen
 * there is a card on screen (MUCKA-WEB-CA).
 *
 * The phone bar marks here too, for the same reason in time rather than
 * place: the probe looks every five seconds, and a card the bar showed for
 * three seconds between two looks was reported as never drawn (MUCKA-WEB-CA,
 * 21 Oct). Only ever a sighting; the beacon is the Actions surfaces' alone.
 */
const sighted = new Set<string>();

export function markCardSighted(id: string): void {
  if (id) sighted.add(id);
}

export function wasCardSighted(id: string): boolean {
  return sighted.has(id);
}

/** Test seam. Nothing in the app calls this. */
export function resetReportedCards(): void {
  reported.clear();
  sighted.clear();
}

export class CardSeenQueue {
  private pending = new Set<string>();

  /** Returns true if this id is new and was queued. */
  add(id: string): boolean {
    if (!id || reported.has(id) || this.pending.has(id)) return false;
    this.pending.add(id);
    return true;
  }

  get size(): number {
    return this.pending.size;
  }

  /**
   * Hand over what is waiting and mark it reported.
   *
   * 🚨 Marked reported on TAKE, not on a successful response. A retry loop on a
   * telemetry beacon is a way to turn a bad network into a flood of duplicate
   * writes, and the cost of dropping one sighting is one card in a rate; the
   * cost of retrying is a request storm on the surface an owner is using.
   */
  take(): string[] {
    if (!this.pending.size) return [];
    const out = [...this.pending].slice(0, SEEN_MAX_BATCH);
    for (const id of out) {
      this.pending.delete(id);
      reported.add(id);
    }
    return out;
  }
}
